/**
 * MemberFfsContinuityPanel — "After 1 Jan 2027" (TRUS-729).
 *
 * On 1 January 2027 Medi-Cal members with Unsatisfactory Immigration Status
 * move from managed care to fee-for-service. ECM and Community Supports exist
 * only inside managed care, so they end. This panel tells the case worker, for
 * this member, what can continue under which FFS code family and what will
 * need community funding.
 *
 * Backed by GET /api/members/{id}/ffs-continuity. The server returns codes
 * (service_key, continuity, ffs_family, conditions, reason_code), never prose;
 * every sentence is composed here so it renders in the reader's language.
 *
 * SHOWN ONLY FOR COHORT MEMBERS. The cohort flag comes from her latest
 * eligibility check's aid-code scope (RESTRICTED / EMERGENCY_ONLY). That is a
 * proxy, not immigration status, and the panel says so in those words.
 * Not in the cohort, no eligibility on file, or no access: the panel renders
 * nothing. It is advisory, so a failed load is silent rather than an alarm on
 * every profile.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { AlertTriangle, CalendarClock, CheckCircle2, HelpCircle, XCircle } from 'lucide-react';

interface BenefitsScope {
  value: string | null;
  aid_code: string | null;
  eligibility_status: string | null;
  verified_at: string | null;
  is_proxy: boolean;
  basis: string;
}

interface ServiceContinuity {
  service_key: string;
  kind: string;
  label: string;
  cs_service_code: string | null;
  continuity: 'ffs_path' | 'verify' | 'no_ffs_equivalent' | string;
  ffs_family: string | null;
  ffs_codes: string[];
  ffs_modifiers: string[];
  conditions: string[];
  needs_community_funding: boolean;
}

interface EnrollmentContinuity {
  enrollment_id: number;
  program_id: number | null;
  program_name: string | null;
  program_kind: string;
  enrollment_status: string | null;
  services: ServiceContinuity[];
}

interface FfsContinuity {
  member_id: number;
  map_version: string;
  benefits_scope: BenefitsScope | null;
  transition: { date: string; in_cohort: boolean; reason_code: string; basis: string };
  enrollments: EnrollmentContinuity[];
  summary: { continues: number; verify: number; needs_community_funding: number };
}

function when(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function MemberFfsContinuityPanel({ memberId }: { memberId: number | string }) {
  const { t } = useTranslation();
  const [data, setData] = useState<FfsContinuity | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    authFetchJson<FfsContinuity>(`/api/members/${memberId}/ffs-continuity`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        // Advisory panel: a 403/404/500 renders nothing rather than an alarm.
        // That includes 403 capability_disabled when the org does not have
        // ffs_transition (TRUS-739): data stays null, so the panel is absent.
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  if (!data || !data.transition?.in_cohort) return null;

  // Every label is a literal t() call so the i18n coverage guard can see it.
  const serviceLabels: Record<string, string> = {
    ecm_care_management: t('ffsContinuity.svcEcmCareManagement', 'ECM care management'),
    ecm_outreach_navigation: t('ffsContinuity.svcEcmOutreachNavigation', 'ECM outreach and navigation'),
    ecm_complex_care: t('ffsContinuity.svcEcmComplexCare', 'ECM for members with complex needs'),
    ecm_behavioral_health: t('ffsContinuity.svcEcmBehavioralHealth', 'ECM with behavioral health overlap'),
    perinatal_doula: t('ffsContinuity.svcPerinatalDoula', 'Perinatal support (doula)'),
    cs_housing_transition_navigation: t('ffsContinuity.svcHousingTransitionNavigation', 'Housing Transition Navigation'),
    cs_housing_deposits: t('ffsContinuity.svcHousingDeposits', 'Housing Deposits'),
    cs_housing_tenancy_sustaining: t('ffsContinuity.svcHousingTenancySustaining', 'Housing Tenancy & Sustaining Services'),
    cs_short_term_post_hospitalization_housing: t(
      'ffsContinuity.svcPostHospitalizationHousing',
      'Short-Term Post-Hospitalization Housing',
    ),
    cs_recuperative_care: t('ffsContinuity.svcRecuperativeCare', 'Recuperative Care (Medical Respite)'),
    cs_respite: t('ffsContinuity.svcRespite', 'Respite Services'),
    cs_day_habilitation: t('ffsContinuity.svcDayHabilitation', 'Day Habilitation Programs'),
    cs_nursing_facility_transition_diversion: t(
      'ffsContinuity.svcNursingFacilityDiversion',
      'Nursing Facility Transition/Diversion to Assisted Living',
    ),
    cs_community_transition: t('ffsContinuity.svcCommunityTransition', 'Community Transition Services'),
    cs_personal_care_homemaker: t('ffsContinuity.svcPersonalCareHomemaker', 'Personal Care & Homemaker Services'),
    cs_environmental_accessibility: t(
      'ffsContinuity.svcEnvironmentalAccessibility',
      'Environmental Accessibility Adaptations (Home Modifications)',
    ),
    cs_medically_tailored_meals: t('ffsContinuity.svcMedicallyTailoredMeals', 'Medically Tailored Meals'),
    cs_sobering_centers: t('ffsContinuity.svcSoberingCenters', 'Sobering Centers'),
    cs_asthma_remediation: t('ffsContinuity.svcAsthmaRemediation', 'Asthma Remediation'),
    cs_transitional_rent: t('ffsContinuity.svcTransitionalRent', 'Transitional Rent'),
    cs_unidentified: t('ffsContinuity.svcUnidentified', 'Community Supports service (category not recorded)'),
  };

  const familyLabels: Record<string, string> = {
    CARE_MGMT_EDUCATION: t('ffsContinuity.famCareMgmtEducation', 'Self-management education'),
    COMMUNITY_HEALTH_INTEGRATION: t('ffsContinuity.famChi', 'Community Health Integration (CHW)'),
    CHRONIC_CARE_MANAGEMENT: t('ffsContinuity.famCcm', 'Chronic care management'),
    PSYCHIATRIC_COLLABORATIVE_CARE: t('ffsContinuity.famCocm', 'Psychiatric collaborative care'),
    DOULA: t('ffsContinuity.famDoula', 'Doula services (E/M codes)'),
  };

  const conditionLabels: Record<string, string> = {
    chi_initiating_visit_6mo_sdoh: t(
      'ffsContinuity.condChiInitiatingVisit',
      'Needs an initiating visit with a licensed provider in the last 6 months documenting at least one unmet social need.',
    ),
    chi_60min_unit_31min_minimum_monthly: t(
      'ffsContinuity.condChiTime',
      '60 minutes per unit; at least 31 documented minutes for one unit, counted monthly.',
    ),
    licensed_billing_provider: t('ffsContinuity.condLicensedProvider', 'Billed under a licensed Medi-Cal FFS provider.'),
    doula_enrolled_medi_cal_provider: t(
      'ffsContinuity.condDoulaEnrolled',
      'The doula must be an enrolled Medi-Cal provider.',
    ),
    confirm_ffs_coverage_for_cohort: t(
      'ffsContinuity.condConfirmCoverage',
      'Confirm FFS coverage for this member before relying on it.',
    ),
  };

  const kindLabels: Record<string, string> = {
    ECM: t('ffsContinuity.kindEcm', 'Enhanced Care Management'),
    CS: t('ffsContinuity.kindCs', 'Community Supports'),
    PERINATAL: t('ffsContinuity.kindPerinatal', 'Perinatal support'),
  };

  const badge = (continuity: string) => {
    if (continuity === 'ffs_path') {
      return {
        icon: <CheckCircle2 className="h-3.5 w-3.5" />,
        text: t('ffsContinuity.badgeContinues', 'Continues under FFS'),
        className: 'bg-green-100 text-green-800',
      };
    }
    if (continuity === 'verify') {
      return {
        icon: <HelpCircle className="h-3.5 w-3.5" />,
        text: t('ffsContinuity.badgeVerify', 'Verify FFS coverage'),
        className: 'bg-amber-100 text-amber-800',
      };
    }
    return {
      icon: <XCircle className="h-3.5 w-3.5" />,
      text: t('ffsContinuity.badgeNoFfs', 'No FFS equivalent — needs community funding'),
      className: 'bg-red-100 text-red-800',
    };
  };

  const scope = data.benefits_scope;

  return (
    <section
      className="mb-6 rounded-md border border-amber-300 bg-amber-50/60 p-4 dark:border-amber-700 dark:bg-amber-950/30"
      aria-labelledby="ffs-continuity-title"
    >
      <div className="flex items-start gap-2">
        <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <h3 id="ffs-continuity-title" className="text-base font-semibold">
            {t('ffsContinuity.title', 'After 1 Jan 2027')}
          </h3>
          <p className="mt-1 text-sm">
            {t(
              'ffsContinuity.intro',
              'Her Medi-Cal aid code suggests she may move from managed care to fee-for-service on 1 January 2027. ECM and Community Supports end for members who move.',
            )}
          </p>
          <p className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {t(
                'ffsContinuity.proxyNote',
                'Based on her latest eligibility check (aid code {{aidCode}}, scope {{scope}}, checked {{date}}). This is an aid-code signal, not immigration status. Confirm before acting.',
                {
                  aidCode: scope?.aid_code ?? '—',
                  scope: scope?.value ?? '—',
                  date: when(scope?.verified_at ?? null),
                },
              )}
            </span>
          </p>
        </div>
      </div>

      {data.enrollments.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          {t('ffsContinuity.noEnrollments', 'She has no current ECM or Community Supports enrollments.')}
        </p>
      ) : (
        <>
          <p className="mt-3 text-sm font-medium">
            {t(
              'ffsContinuity.summary',
              '{{continues}} can continue under FFS · {{verify}} to verify · {{funding}} need community funding',
              {
                continues: data.summary.continues,
                verify: data.summary.verify,
                funding: data.summary.needs_community_funding,
              },
            )}
          </p>
          <div className="mt-3 space-y-3">
            {data.enrollments.map((e) => (
              <div key={e.enrollment_id} className="rounded-md border bg-background p-3">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">{e.program_name || kindLabels[e.program_kind] || e.program_kind}</span>
                  <span className="text-xs text-muted-foreground">{kindLabels[e.program_kind] ?? e.program_kind}</span>
                </div>
                <ul className="mt-2 space-y-2">
                  {e.services.map((s) => {
                    const b = badge(s.continuity);
                    const codes = [...s.ffs_codes];
                    return (
                      <li key={s.service_key} className="text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{serviceLabels[s.service_key] ?? s.label}</span>
                          {s.cs_service_code && (
                            <span className="font-mono text-xs text-muted-foreground">{s.cs_service_code}</span>
                          )}
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${b.className}`}
                          >
                            {b.icon}
                            {b.text}
                          </span>
                        </div>
                        {s.ffs_family && (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {t('ffsContinuity.familyLine', 'FFS: {{family}}', {
                              family: familyLabels[s.ffs_family] ?? s.ffs_family,
                            })}
                            {codes.length > 0 &&
                              ` · ${t('ffsContinuity.codesLine', 'Codes {{codes}}', { codes: codes.join(', ') })}`}
                            {s.ffs_modifiers.length > 0 &&
                              ` · ${t('ffsContinuity.modifierLine', 'Modifier {{mods}}', {
                                mods: s.ffs_modifiers.join(', '),
                              })}`}
                          </div>
                        )}
                        {s.conditions.length > 0 && (
                          <ul className="mt-0.5 list-disc pl-5 text-xs text-muted-foreground">
                            {s.conditions.map((c) => (
                              <li key={c}>{conditionLabels[c] ?? c}</li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
